class CollectionFabricSamples extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.cartState = [];
    this.container = this;
    this.contentContainer = this.container.querySelector(
      ".collection__page-content-container",
    );
    this.productsContainer = this.container.querySelector(
      ".content-container__subcategories",
    );
    this.cartBar = document.querySelector(".swatches-cart-container");
    this.maxProductInCart = parseInt(this.container.dataset.maxProductInCart);
    this.orderSwatchesBtn = this.cartBar.querySelector(
      ".cart-container__button-container button",
    );
    this.cartBarSwatchesContainer = this.cartBar.querySelector(
      ".products-container__swatchers",
    );
    this.cartBarSwatchesMobileContainer = this.cartBar.querySelector(
      ".products-container__swatchers-mobile",
    );
    this.emptyBarSwatchesText = this.cartBar.querySelector(
      "[data-empty-swatches-container]",
    );
    this.productsContainerSwatches = document.querySelector(
      ".cart-container__products-container",
    );
    this.filtrationBar = document.querySelector("#swatch_select_head");
    this.quickBuyContainer = this.container.querySelector(
      ".content-container__quick-buy-container",
    );
    this.quickBuyAtcBtn = this.quickBuyContainer.querySelector(
      ".quick-buy__buttons .atc-button",
    );
    this.currentCollectionLink = new URL(window.location.href);
    this.linkParams = new URLSearchParams(this.currentCollectionLink.search);
    this.fetchRequestToken = 0;
    this.deepLinkHandled = false;

    this.filterTypeMap = {
      "filter.p.m.fabric_info.fabric_samples_color": "colors",
      "filter.p.m.fabric_info.fabric_samples_material_type": "materials",
      "filter.p.m.fabric_info.fabric_samples_features": "features",
      "filter.p.m.fabric_info.fabric_samples_pricing_tier": "pricing",
      "filter.p.m.fabric_info.fabric_samples_fabric_name": "names",
    };
    this.referenceProducts = new Map();
    this.referenceCounts = {};
    this.currentCounts = {};
    this.checkboxSelector =
      '.fabric-samples-filter-content input[type="checkbox"]';
    this.desktopCartFadeObserver = null;
    this.desktopCartFadeTarget = null;
    this.desktopCartFadeTransitionHandler = null;
    this.desktopCartShouldShow = true;

    this.setCartBarListeners();
    this.setSortEventListeners();
    this.setQuickBuyAtcBtnListener(this.quickBuyAtcBtn);
    this.initAnimateOpenClose();
    this.setMobileFiltersSortListeners();
    this.detailsToggle();
    this.initMobileDrawerToggle();
    this.initSortDropdownSync();
    this.addResizeListener();
    this.initDesktopCartFadeObserver();
    this.initCheckboxEnterKey();
    this.initLimitModal();
    this.initDelegatedListeners();
    this.initPopstateHandler();

    this.initRenderer();
  }

  initCheckboxEnterKey() {
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      const checkbox = e.target.closest(this.checkboxSelector);
      if (!checkbox) return;
      e.preventDefault();
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  initDelegatedListeners() {
    document.querySelector(".fabric-samples-selected-filters")
      ?.addEventListener("click", (e) => {
        const removeBtn = e.target.closest(".fabric-samples-filter-remove");
        if (removeBtn) {
          this.removeFilter(removeBtn);
          return;
        }
        if (e.target.closest(".fabric-samples-filter-clear-all")) {
          this.clearAllFilters(e);
        }
      });

    const filterHost = document.querySelector("#swatch_select_head");
    if (filterHost) {
      filterHost.addEventListener("click", (e) => {
        if (e.target.closest(".fabric-samples-filter-apply") || e.target.closest(".fabric-samples-sort-apply")) {
          this.toggleFilterState(e);
          if (window.innerWidth < 981) {
            const menu = document.querySelector("#swatch_select_head");
            if (menu && menu.classList.contains("is-visible")) {
              this.hideMenu(menu);
            }
          }
          return;
        }
        if (e.target.closest(".fabric-samples-filter-reset")) {
          this.clearFilters(e);
        }
      });
    }
  }

  initPopstateHandler() {
    window.addEventListener("popstate", () => {
      const url = new URL(window.location.href);

      document.querySelectorAll(this.checkboxSelector).forEach((cb) => {
        cb.checked = false;
      });

      document.querySelectorAll(".fabric-samples-filter-values[data-filter-name]").forEach((group) => {
        const activeValues = url.searchParams.getAll(group.dataset.filterName);
        if (activeValues.length === 0) return;
        group.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
          if (activeValues.includes(cb.value)) cb.checked = true;
        });
      });

      const sortBy = url.searchParams.get("sort_by") || "default";
      const sortSelect = document.querySelector(".fabric-samples-sort-select");
      if (sortSelect) sortSelect.value = sortBy;

      this.toggleFilterState({ preventDefault() {} }, { pushHistory: false });
    });
  }

  async initRenderer() {
    const jsonEl = this.querySelector("#fabric-samples-data");
    if (!jsonEl) return;

    await window.FabricSamplesRenderer.init(jsonEl);

    this.setProductCardListeners(this.container);
    this.modalInitialization();
    this.initProductInCart();
    this.initFilterCounting();

    // Page-1 / single-page fabrics are in the renderer's maps now.
    this.tryOpenDeepLinkedFabric();

    const rendererData = window.FabricSamplesRenderer.getData();
    const hasAdditionalPages = (rendererData?.pagination?.totalPages || 1) > 1;
    if (!hasAdditionalPages) {
      this.updateResultsCount();
    }

    window.FabricSamplesRenderer.onFullLoad(() => {
      this.setProductCardListeners(this.container);
      this.initProductInCart();
      this.initFilterCounting();
      this.updateResultsCount();
      // Catch fabrics that live on a later-loaded page.
      this.tryOpenDeepLinkedFabric();
    });

    window.FabricSamplesRenderer.onMasterReady(() => {
      this.initFilterCounting();
    });
  }

  // Turns a display name into a URL-safe slug, matching how the deep-link
  // hash is authored. "Bella Navy" -> "bella-navy".
  slugify(value) {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Reads the target fabric slug from the URL hash, normalized the same way
  // as the display title. /collections/fabric-samples#bella-navy -> "bella-navy".
  getDeepLinkSlug() {
    const raw = window.location.hash.replace(/^#/, "");
    if (!raw) return "";
    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch (e) {
      // Keep raw value on malformed escape sequences.
    }
    return this.slugify(decoded);
  }

  // Reflects the currently-shown fabric in the URL hash (e.g. #amici-moss for
  // "Amici Moss") so the link is copy/paste-shareable and re-openable by the
  // deep-link handler. replaceState keeps it out of the back-button history and
  // preserves the path + any filter query string, matching the close handler.
  syncUrlToProduct(product) {
    if (!product) return;
    const slug = this.slugify(product.title);
    if (!slug) return;
    history.replaceState(null, "", "#" + slug);
  }

  // Opens the detail modal for the hash-linked fabric once its swatch has
  // rendered. The hash uses the displayed fabric name (the modal/card title),
  // e.g. #bella-navy for "Bella Navy" -- not the product handle, which can
  // carry suffixes like "-sample". The handle is still accepted as a fallback.
  // Safe to call multiple times across the load lifecycle: it no-ops once
  // handled, and stays unhandled until a match is found so a later (paginated)
  // render can still resolve it.
  tryOpenDeepLinkedFabric() {
    if (this.deepLinkHandled) return;

    const slug = this.getDeepLinkSlug();
    if (!slug) return;

    const blockProductMap = window.FabricSamplesRenderer.getBlockProductMap();
    if (!blockProductMap) return;

    for (const [blockId, blockData] of blockProductMap) {
      const product = (blockData.products || []).find(
        (p) => this.slugify(p.title) === slug || p.handle === slug,
      );
      if (product) {
        this.deepLinkHandled = true;
        this.openProductModal(product.id, blockId, document.body);
        return;
      }
    }
  }

  updateResultsCount() {
    const el = document.querySelector(".fabric-samples-results");
    if (el) {
      el.classList.remove("is-loading");
      el.textContent = `${this.container.querySelectorAll(".fabric-samples-card").length} Results`;
    }
  }

  initFilterCounting() {
    this.buildReferenceData();
    this.updateCounts();
  }

  buildReferenceData() {
    const rendererData = window.FabricSamplesRenderer.getData();
    if (!rendererData) return;

    const sourceProducts = window.FabricSamplesRenderer.getAllProducts() || rendererData.products;

    this.referenceProducts = new Map();

    sourceProducts.forEach((product, index) => {
      this.referenceProducts.set(index, {
        colors: product.colors || [],
        materials: product.materials || [],
        features: (product.features || []).map((f) => f.name),
        pricing: product.pricing || [],
        names: product.fabricNames || [],
      });
    });

    Object.values(this.filterTypeMap).forEach((type) => {
      this.referenceCounts[type] = {};
      const values = new Set();
      this.referenceProducts.forEach((product) => {
        product[type].forEach((v) => values.add(v.trim()));
      });
      values.forEach((value) => {
        this.referenceCounts[type][value] = Array.from(
          this.referenceProducts.values(),
        ).filter((p) => p[type].some((v) => v.trim() === value.trim())).length;
      });
    });
  }

  getCheckedFilters() {
    const checked = {};
    document
      .querySelectorAll(this.checkboxSelector + ":checked")
      .forEach((box) => {
        const type = this.getFilterType(box);
        if (!checked[type]) checked[type] = [];
        checked[type].push(box.dataset.name.trim());
      });
    return checked;
  }

  updateCounts() {
    const checkedFilters = this.getCheckedFilters();
    this.currentCounts = this.calculateNewCounts(checkedFilters);
    this.injectCounts(this.currentCounts);
  }

  calculateNewCounts(checkedFilters) {
    const newCounts = {};

    document.querySelectorAll(this.checkboxSelector).forEach((checkbox) => {
      const value = checkbox.dataset.name;
      const type = this.getFilterType(checkbox);

      if (!newCounts[type]) newCounts[type] = {};

      if (Object.keys(checkedFilters).length === 0) {
        newCounts[type][value] = this.referenceCounts[type][value];
        return;
      }

      let matchingProducts = Array.from(this.referenceProducts.values()).filter(
        (product) => {
          if (!product[type].includes(value)) return false;

          return Object.entries(checkedFilters).every(
            ([filterType, selectedValues]) => {
              if (filterType === type && type !== "features") return true;

              if (filterType === "features") {
                return selectedValues.every((v) =>
                  product[filterType].includes(v),
                );
              } else {
                return selectedValues.some((v) =>
                  product[filterType].includes(v),
                );
              }
            },
          );
        },
      );

      newCounts[type][value] = matchingProducts.length;
    });

    return newCounts;
  }

  getFilterType(checkbox) {
    const filterGroup = checkbox.closest(".fabric-samples-filter-values");
    const fullFilterName = filterGroup.dataset.filterName;
    return this.filterTypeMap[fullFilterName];
  }

  injectCounts(countsObject) {
    document.querySelectorAll(this.checkboxSelector).forEach((checkbox) => {
      const value = checkbox.dataset.name;
      const type = this.getFilterType(checkbox);
      const count = countsObject[type]?.[value] ?? 0;

      checkbox.disabled = count === 0 && !checkbox.checked;

      checkbox
        .closest(".fabric-samples-filter-value")
        .querySelector(".fabric-samples-filter-count").textContent =
        `(${count})`;
    });
  }

  detailsToggle() {
    let detailsElements = document.querySelectorAll(
      ".fabric-samples-filter-details",
    );
    let sortDetailsElements = document.querySelectorAll(
      ".fabric-samples-sort-details",
    );

    document.addEventListener("click", (event) => {
      const clickedElement = event.target;
      const isClickInsideFilter =
        clickedElement.closest(".fabric-samples-filter-details") ||
        clickedElement.closest(".fabric-samples-filter-content");
      const isClickOnFilterSummary = clickedElement.closest(
        ".fabric-samples-filter-summary",
      );
      const isClickInsideSort =
        clickedElement.closest(".fabric-samples-sort-details") ||
        clickedElement.closest(".fabric-samples-sort-content");
      const isClickOnSortSummary = clickedElement.closest(
        ".fabric-samples-sort-summary",
      );

      if (!isClickInsideFilter && !isClickOnFilterSummary) {
        detailsElements.forEach((details) => {
          if (details.open) {
            details.open = false;
          }
        });
      }

      if (!isClickInsideSort && !isClickOnSortSummary) {
        sortDetailsElements.forEach((details) => {
          if (details.open) {
            details.open = false;
          }
        });
      }
    });

    detailsElements.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (details.open && window.innerWidth > 980) {
          detailsElements.forEach((otherDetails) => {
            if (otherDetails !== details && otherDetails.open) {
              otherDetails.open = false;
            }
          });
          sortDetailsElements.forEach((sortDetails) => {
            if (sortDetails.open) {
              sortDetails.open = false;
            }
          });
        }
      });
    });

    sortDetailsElements.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (details.open && window.innerWidth > 980) {
          sortDetailsElements.forEach((otherDetails) => {
            if (otherDetails !== details && otherDetails.open) {
              otherDetails.open = false;
            }
          });
          detailsElements.forEach((filterDetails) => {
            if (filterDetails.open) {
              filterDetails.open = false;
            }
          });
        }
      });
    });
  }

  closeAllDetails() {
    let detailsElements = document.querySelectorAll(
      ".fabric-samples-filter-details",
    );
    detailsElements.forEach((details) => {
      if (details.open) {
        details.open = false;
      }
    });
  }

  initSortDropdownSync() {
    const originalSelect = document.querySelector(
      ".fabric-samples-sort-select",
    );
    const styledRadios = document.querySelectorAll(
      '.fabric-samples-sort-value input[type="radio"]',
    );
    const clearSelectionButtons = document.querySelectorAll(
      ".fabric-samples-sort-reset",
    );

    if (!originalSelect || styledRadios.length === 0) return;

    const currentSortParam = new URL(window.location.href).searchParams.get(
      "sort_by",
    );
    const optionValues = Array.from(originalSelect.options).map(
      (option) => option.value,
    );

    if (currentSortParam && optionValues.includes(currentSortParam)) {
      originalSelect.value = currentSortParam;
    }

    const syncStyledRadiosFromSelect = () => {
      const selectedValue = originalSelect.value;
      styledRadios.forEach((radio) => {
        radio.checked = radio.value === selectedValue;
      });
    };

    syncStyledRadiosFromSelect();

    const closeOpenSortDetails = () => {
      document.querySelectorAll(".fabric-samples-sort-details").forEach((el) => {
        if (el.open) el.open = false;
      });
    };

    styledRadios.forEach((radio) => {
      radio.addEventListener("change", (event) => {
        if (event.target.checked) {
          originalSelect.value = event.target.value;
          const changeEvent = new Event("change", { bubbles: true });
          originalSelect.dispatchEvent(changeEvent);
          closeOpenSortDetails();
        }
      });
    });

    originalSelect.addEventListener("change", (event) => {
      syncStyledRadiosFromSelect();
    });

    clearSelectionButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        styledRadios.forEach((radio) => {
          radio.checked = false;
        });
        originalSelect.value = "default";
        const changeEvent = new Event("change", { bubbles: true });
        originalSelect.dispatchEvent(changeEvent);
        closeOpenSortDetails();
      });
    });
  }

  initMobileDrawerToggle() {
    const cartContainer = document.querySelector(".fabric-samples-cart");
    const cartHeader = cartContainer.querySelector(
      ".fabric-samples-cart-header",
    );
    let isDrawerOpen = false;

    const toggleDrawer = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const openTransform = "translateY(0px)";
      const closedTransform = "translateY(calc(100% - 90px))";

      if (isDrawerOpen) {
        cartContainer.animate(
          [{ transform: openTransform }, { transform: closedTransform }],
          { duration: 300, easing: "ease-out", fill: "forwards" }
        );
      } else {
        cartContainer.animate(
          [{ transform: closedTransform }, { transform: openTransform }],
          { duration: 300, easing: "ease-out", fill: "forwards" }
        );
      }

      isDrawerOpen = !isDrawerOpen;
      cartHeader.setAttribute("aria-expanded", String(isDrawerOpen));
    };

    cartHeader.addEventListener("click", toggleDrawer);
    cartHeader.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleDrawer(e);
      }
    });
  }

  initAnimateOpenClose() {
    const details = document.querySelector(".collapsible-row details");
    const summary = details.querySelector("summary");
    details.addEventListener("toggle", (event) => {
      const isOpen = details.open;
      const mainProductContainer = this.productsContainerSwatches.querySelector(
        ".products-container__swatchers",
      );
      const children = mainProductContainer.querySelector(".swatch__container");
      if (children) {
        if (isOpen) {
          this.productsContainerSwatches.classList.remove("close-animation");
          this.productsContainerSwatches.classList.add("open-animation");
        }
      }
    });
    summary.addEventListener("click", (event) => {
      const isOpen = details.open;
      const mainProductContainer = this.productsContainerSwatches.querySelector(
        ".products-container__swatchers",
      );
      const children = mainProductContainer.querySelector(".swatch__container");
      if (children) {
        if (isOpen) {
          this.productsContainerSwatches.classList.remove("open-animation");
          this.productsContainerSwatches.classList.add("close-animation");
        }
      }
    });
  }

  setProductCardListeners(container) {
    const productCards = container.querySelectorAll(
      "div.product-card-container__product-card",
    );
    productCards.forEach((card) => {
      const handleMostPopularAction = (event) => {
        if (event.target.closest(".product-card__atc-btn")) {
          this.toggleCartState(card, event);
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const productVariantId = parseInt(card.dataset.productId);
        const product =
          window.FabricSamplesRenderer.getProductByVariantId(
            productVariantId,
          );
        if (!product) return;

        const blockProductMap =
          window.FabricSamplesRenderer.getBlockProductMap();
        for (const [blockId, { block, products }] of blockProductMap) {
          if (block.title === "Most Popular") continue;
          if (products.some((p) => p.id === product.id)) {
            this.openProductModal(product.id, blockId, card);
            return;
          }
        }
      };

      if (card.classList.contains("most-popular-card")) {
        card.addEventListener("click", handleMostPopularAction);
        card.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleMostPopularAction(event);
          }
        });
      } else {
        card.addEventListener("mouseenter", (event) =>
          this.quickBuyCardChange(event),
        );
        card.addEventListener("click", (event) =>
          this.toggleCartState(card, event),
        );
        card.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            this.toggleCartState(card, event);
          }
        });
      }
    });
  }

  quickBuyCardChange(event) {
    const quickBuyCard = document.querySelector(
      "div.content-container__quick-buy",
    );
    const card = event.target.closest(
      ".product-card-container__product-card",
    );
    if (!card) return;

    const variantId = parseInt(card.dataset.productId);
    const product =
      window.FabricSamplesRenderer.getProductByVariantId(variantId);
    if (!product) return;

    const productCardImageHtml = card.querySelector(
      ".product-card__image-wrapper>img",
    ).outerHTML;

    const quickBuyTitle = quickBuyCard.querySelector(".quick-buy__title");
    const quickBuyDesc = quickBuyCard.querySelector(".quick-buy__description");
    const quickBuyImage = quickBuyCard.querySelector(
      ".quick-buy__image-wrapper",
    );
    const quickBuyButtonsContainer = quickBuyCard.querySelector(
      ".quick-buy__buttons",
    );
    const quickBuyAtcBtn = quickBuyCard.querySelector(
      ".quick-buy__buttons .atc-button",
    );
    const quickBuyDetailsBtn = quickBuyCard.querySelector(
      "button.details-button",
    );
    const isInCart = this.cartState.find(
      ({ productId }) => productId === variantId,
    );

    quickBuyTitle.innerText = product.title;
    quickBuyDesc.innerHTML = product.shortDescription || "";
    quickBuyImage.innerHTML = productCardImageHtml;
    quickBuyButtonsContainer.setAttribute(
      "data-product-id",
      product.variantId,
    );

    const blockId = card.closest("[data-block]")?.dataset.block;
    if (quickBuyDetailsBtn) {
      quickBuyDetailsBtn.setAttribute(
        "data-product-id-trigger",
        product.id,
      );
      if (blockId)
        quickBuyDetailsBtn.setAttribute("data-block-id", blockId);
    }

    quickBuyAtcBtn.classList.toggle("added-to-cart", !!isInCart);
  }

  setCartBarListeners() {
    this.cartBar.addEventListener(
      "click",
      this.removeProductFromCart.bind(this),
    );
    this.cartBar.addEventListener("keydown", (e) => {
      if ((e.key === "Enter" || e.key === " ") && e.target.closest(".fabric-samples-cart-remove")) {
        e.preventDefault();
        this.removeProductFromCart(e);
      }
    });
    this.orderSwatchesBtn.addEventListener("click", this.submitCart.bind(this));
  }

  setMobileFiltersSortListeners() {
    const buttons = [
      {
        selector: ".fabric-samples-mobile-filter-icon",
        action: this.toggleMobileFiltersMenu.bind(this),
      },
      {
        selector: ".swatch_select_head_mobile_close",
        action: this.toggleMobileFiltersMenu.bind(this),
      },
    ];

    buttons.forEach(({ selector, action }) => {
      const button = document.querySelector(selector);
      if (button) button.addEventListener("click", action);
    });

    window.addEventListener("resize", this.handleResize.bind(this));
  }

  toggleMobileMenu(menuSelector) {
    const menu = document.querySelector(menuSelector);
    if (!menu) return;

    if (menu.classList.contains("is-visible")) {
      this.hideMenu(menu);
    } else {
      this.showMenu(menu);
    }
  }

  hideMenu(menu) {
    document.body.style.overflow = "";
    document.body.style.overscrollBehavior = "";
    menu.classList.add("is-animatable");
    menu.classList.remove("is-visible");
  }

  showMenu(menu) {
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    menu.classList.add("is-animatable");
    menu.classList.add("is-visible");
  }

  handleResize() {
    const menus = [document.querySelector("#swatch_select_head")];

    menus.forEach((menu) => {
      if (!menu) return;

      if (window.innerWidth > 980) {
        document.body.style.overflow = "";
        document.body.style.overscrollBehavior = "";
        menu.classList.remove("is-visible");
        menu.classList.remove("is-animatable");
      }
    });
  }

  toggleMobileFiltersMenu() {
    this.toggleMobileMenu("#swatch_select_head");
  }


  clearFilters(event) {
    event.target
      .closest(".fabric-samples-filter-content")
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    this.toggleFilterState(event);
    this.updateCounts();
  }

  clearAllFilters(event) {
    document
      .querySelectorAll('.fabric-samples-filter-value input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });

    this.toggleFilterState(event);
    this.updateCounts();
  }

  escapeAttr(str) {
    if (!str) return "";
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#039;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  escapeHtml(str) {
    if (!str) return "";
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  renderSelectedFilterPills() {
    const container = document.querySelector(".fabric-samples-selected-filters");
    if (!container) return;

    const checked = document.querySelectorAll(this.checkboxSelector + ":checked");
    if (checked.length === 0) {
      container.innerHTML = "";
      return;
    }

    const closeIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#202020" style="height:16px; width:16px"><path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';

    let html = "";
    checked.forEach((cb) => {
      const filterName = cb.closest(".fabric-samples-filter-values")?.dataset.filterName || "";
      html += `<button class="fabric-samples-filter-remove" data-value="${this.escapeAttr(cb.value)}" data-filter-name="${this.escapeAttr(filterName)}">
        ${this.escapeHtml(cb.dataset.name)} ${closeIcon}
      </button>`;
    });
    html += '<button class="fabric-samples-filter-clear-all">CLEAR ALL FILTERS</button>';
    container.innerHTML = html;
  }

  removeFilter(btnOrEvent) {
    const btn = btnOrEvent instanceof Event
      ? btnOrEvent.target.closest(".fabric-samples-filter-remove")
      : btnOrEvent;
    const value = btn?.dataset?.value;
    const filterName = btn?.dataset?.filterName;

    if (value) {
      const selector = filterName
        ? `.fabric-samples-filter-values[data-filter-name="${filterName}"] .fabric-samples-filter-value[data-value="${value}"]`
        : `.fabric-samples-filter-value[data-value="${value}"]`;
      const el = document.querySelector(selector);
      if (el) el.querySelector('input[type="checkbox"]').checked = false;
    }

    this.toggleFilterState({ preventDefault() {} });
  }

  getCurrentPathUrl() {
    return new URL(window.location.href);
  }

  getFilterParamNames() {
    const filterParamNames = new Set();
    const filterValuesGroups = document.querySelectorAll(
      ".fabric-samples-filter-values[data-filter-name]",
    );

    filterValuesGroups.forEach((group) => {
      const filterName = group.dataset.filterName;
      if (!filterName) return;
      filterParamNames.add(filterName);
    });

    return filterParamNames;
  }

  buildShareableUrlFromLinkParams() {
    const nextUrl = this.getCurrentPathUrl();
    const mergedParams = new URLSearchParams(nextUrl.search);

    this.getFilterParamNames().forEach((paramName) => {
      mergedParams.delete(paramName);
    });

    mergedParams.delete("sort_by");
    mergedParams.delete("view");

    this.linkParams.forEach((value, key) => {
      mergedParams.append(key, value);
    });

    const query = mergedParams.toString();
    nextUrl.search = query ? `?${query}` : "";

    return nextUrl;
  }

  updateBrowserUrl(url) {
    const nextUrl =
      url instanceof URL ? url : new URL(url, window.location.origin);
    const currentPathWithSearch = `${window.location.pathname}${window.location.search}`;
    const nextPathWithSearch = `${nextUrl.pathname}${nextUrl.search}`;

    if (currentPathWithSearch === nextPathWithSearch) return;

    history.pushState({}, "", nextPathWithSearch);
    this.currentCollectionLink = this.getCurrentPathUrl();
  }

  toggleFilterState(event, { pushHistory = true } = {}) {
    event.preventDefault();

    this.linkParams = new URLSearchParams();
    document.querySelectorAll(".fabric-samples-filter-details").forEach((fg) => {
      const fc = fg.nextElementSibling;
      if (!fc?.classList.contains("fabric-samples-filter-content")) return;
      const fv = fc.querySelector(".fabric-samples-filter-values");
      if (!fv?.dataset.filterName) return;
      fc.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        this.linkParams.append(fv.dataset.filterName, cb.value);
      });
    });

    const sortSelect = document.querySelector(".fabric-samples-sort-select");
    const sortBy = sortSelect ? sortSelect.value : "default";
    if (sortBy !== "default") {
      this.linkParams.set("sort_by", sortBy);
    }

    const checkedFilters = this.getCheckedFilters();

    const applied = window.FabricSamplesRenderer.applyFilters(checkedFilters, sortBy);
    if (!applied) {
      const nextUrl = this.buildShareableUrlFromLinkParams();
      this.fetchNewResults(nextUrl);
      return;
    }

    this.renderSelectedFilterPills();
    this.initFilterCounting();
    this.setProductCardListeners(this.container);
    this.closeAllDetails();

    this.cartState.forEach(({ productId }) => {
      const card = this.container.querySelector(`[data-product-id="${productId}"]`);
      if (card) this.toggleCardState(card, true);
    });

    document.querySelector(".fabric-samples-results").innerHTML =
      `${this.container.querySelectorAll(".fabric-samples-card").length} Results`;

    if (pushHistory) {
      this.updateBrowserUrl(this.buildShareableUrlFromLinkParams());
    }

    if (window.innerWidth < 981) {
      const menu = document.querySelector("#swatch_select_head");
      if (menu?.classList.contains("is-visible")) this.hideMenu(menu);
    }
  }

  setSortEventListeners() {
    document
      .querySelector(".fabric-samples-sort-select")
      .addEventListener("change", this.toggleFilterState.bind(this));
  }


  fetchNewResults(filterUrl = this.buildShareableUrlFromLinkParams()) {
    const requestToken = ++this.fetchRequestToken;
    const requestUrl =
      filterUrl instanceof URL
        ? filterUrl
        : new URL(filterUrl, window.location.origin);
    const requestUrlString = requestUrl.toString();

    this.productsContainer.classList.toggle("fetching", true);

    const sectionId = this.dataset.sectionId;
    const fetchUrl = new URL(requestUrlString);
    if (sectionId) fetchUrl.searchParams.set("sections", sectionId);

    fetch(fetchUrl.toString())
      .then((response) => sectionId ? response.json() : response.text())
      .then(async (data) => {
        if (requestToken !== this.fetchRequestToken) return;

        const html = sectionId ? data[sectionId] : data;
        const doc = new DOMParser().parseFromString(html, "text/html");

        const jsonEl = doc.getElementById("fabric-samples-data");
        if (jsonEl) {
          const newData = JSON.parse(jsonEl.textContent);
          await window.FabricSamplesRenderer.initFromData(
            newData,
            requestUrlString,
          );
          if (requestToken !== this.fetchRequestToken) return;
        }

        const fabricSamplesSelectedFilters = doc.querySelector(
          ".fabric-samples-selected-filters",
        )?.innerHTML;
        if (fabricSamplesSelectedFilters !== undefined) {
          document.querySelector(
            ".fabric-samples-selected-filters",
          ).innerHTML = fabricSamplesSelectedFilters;
        }

        this.cartState.forEach(({ productId }) => {
          const card = this.container.querySelector(
            `[data-product-id="${productId}"]`,
          );
          if (card) this.toggleCardState(card, true);
        });

        this.initFilterCounting();
        this.setProductCardListeners(this.container);
        this.closeAllDetails();

        document.querySelector(".fabric-samples-results").innerHTML =
          `${this.container.querySelectorAll(".fabric-samples-card").length} Results`;

        this.updateBrowserUrl(requestUrl);
        this.productsContainer.classList.toggle("fetching", false);

        if (window.innerWidth < 981) {
          const menu = document.querySelector("#swatch_select_head");
          if (menu && menu.classList.contains("is-visible")) {
            this.hideMenu(menu);
          }
        }
      })
      .catch(() => {
        if (requestToken === this.fetchRequestToken) {
          this.productsContainer.classList.toggle("fetching", false);
        }
      });
  }

  setQuickBuyAtcBtnListener(atcBtn) {
    atcBtn.addEventListener("click", (e) => {
      const productId = e.target.closest(".quick-buy__buttons").dataset
        .productId;
      const productCard = this.container.querySelector(
        `.product-card-container__product-card[data-product-id="${productId}"]`,
      );
      if (productCard) this.toggleCartState(productCard, e);
    });
  }

  modalInitialization() {
    this.modal = document.getElementById("fabric-samples-modal");
    this.modalContent = this.modal?.querySelector(".fabric-samples-modal-content");
    this.modalTitle = this.modal?.querySelector(".fabric-samples-modal-title");
    this.currentModalBlockId = null;

    if (this.modal) {
      const nativeHide = this.modal.hide.bind(this.modal);
      this.modal.hide = () => {
        this.destroyModalSwiper();
        // Drop the deep-link hash so a manual refresh after closing the
        // modal doesn't immediately re-open it. replaceState avoids adding
        // a history entry.
        if (window.location.hash) {
          history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
        }
        nativeHide();
      };

      this.modal.addEventListener("click", (e) => {
        const cancelBtn = e.target.closest("[data-modal-cancel]");
        if (cancelBtn) {
          this.modal.hide();
        }

        const switchTarget = e.target.closest("[data-switch-product-id]");
        if (switchTarget) {
          const pid = parseInt(switchTarget.dataset.switchProductId);
          const bid = switchTarget.dataset.switchBlockId;
          if (pid && bid) this.switchModalProduct(pid, bid);
        }
      });

      this.modal.addEventListener("keydown", (e) => {
        const switchTarget = e.target.closest("[data-switch-product-id]");
        if (switchTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          const pid = parseInt(switchTarget.dataset.switchProductId);
          const bid = switchTarget.dataset.switchBlockId;
          if (pid && bid) this.switchModalProduct(pid, bid);
        }
      });
    }

    this.container.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-fancybox-trigger]");
      if (!trigger) return;
      if (event.target.closest(".product-card__atc-btn")) return;

      event.preventDefault();
      event.stopPropagation();

      const productId = parseInt(trigger.dataset.productIdTrigger);
      const blockId = trigger.dataset.blockId;

      if (productId && blockId) {
        this.openProductModal(productId, blockId, trigger);
      }
    });

    const detailsBtn = this.quickBuyContainer.querySelector(
      "button.details-button",
    );
    if (detailsBtn) {
      detailsBtn.addEventListener("click", (event) => {
        event.preventDefault();
        const pid = parseInt(detailsBtn.dataset.productIdTrigger);
        const bid = detailsBtn.dataset.blockId;
        if (pid && bid) this.openProductModal(pid, bid, detailsBtn);
      });
    }
  }

  initLimitModal() {
    const limitModal = document.getElementById("reached-limit-modal");
    if (limitModal) {
      limitModal.addEventListener("click", (e) => {
        if (e.target.closest(".fabric-samples-modal-backdrop") || e.target.closest(".limit-modal-close")) {
          limitModal.hidden = true;
        }
      });
    }
  }

  destroyModalSwiper() {
    if (!this.modalContent) return;
    const swiper = this.modalContent.querySelector(".swiper")?.swiper;
    if (swiper) swiper.destroy(true, true);
  }

  openModal(opener) {
    if (!this.modal) return;
    this.modal.show(opener);
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.hide();
  }

  openProductModal(productId, blockId, opener) {
    if (!this.modal || !this.modalContent) return;

    const product = window.FabricSamplesRenderer.getProductById(productId);
    const html = window.FabricSamplesRenderer.buildSingleModalHtml(productId, blockId);
    if (!html) return;

    this.currentModalBlockId = blockId;
    this.modalContent.innerHTML = html;

    if (this.modalTitle && product) {
      this.modalTitle.textContent = product.title;
    }

    this.syncUrlToProduct(product);

    if (!this.modal.hasAttribute("open")) {
      this.openModal(opener);
    }

    this.initModalContent();
  }

  switchModalProduct(productId, blockId) {
    if (!this.modal || !this.modalContent) return;

    const product = window.FabricSamplesRenderer.getProductById(productId);
    if (!product) return;

    this.currentModalBlockId = blockId;
    this.updateModalDetails(product);
    this.syncUrlToProduct(product);
  }

  updateModalDetails(product) {
    const contentEl = this.modalContent;

    if (this.modalTitle) this.modalTitle.textContent = product.title;

    const img = contentEl.querySelector(".fabric-samples-image-container img");
    if (img) {
      img.src = product.imageLarge;
      img.alt = product.imageAlt || product.title;
      img.width = product.imageWidth;
      img.height = product.imageHeight;
    }

    const container = contentEl.querySelector(".fabric-samples-image-container");
    if (container) {
      container.classList.remove("is-zoomed");
      if (img) img.style.transform = "scale(1) translate(0, 0)";
    }

    const featureHtml = window.FabricSamplesRenderer.renderFeatureIcons(product.features || []);
    contentEl.querySelectorAll(".fabric-samples-types-popup").forEach((el) => {
      el.innerHTML = featureHtml;
    });

    contentEl.querySelectorAll(".fabric-samples-description-content").forEach((el) => {
      el.innerHTML = (product.description || "")
        + '<p class="fabric-samples-description-header fabric-samples-description-header--mt">More details:</p>'
        + '<ul><li>Free of PFAS/PFOS &amp; other unwanted &quot;forever&quot; chemicals. Colors may differ in appearance on your screen due to monitor variations. Dye lots may vary.</li></ul>';
    });

    const fabricName = (product.fabricNames || [])[0] || "";
    const navText = contentEl.querySelector(".fabric-samples-details-navigation-text");
    if (navText) navText.textContent = `Other colors in the ${fabricName} collection:`;

    contentEl.querySelectorAll(".sample-navigation-image").forEach((el) => {
      const id = parseInt(el.dataset.switchProductId);
      el.classList.toggle("selected-navigation-image", id === product.id);
    });

    const buttonsContainer = contentEl.querySelector(".quick-buy__buttons");
    if (buttonsContainer) {
      buttonsContainer.dataset.productId = product.variantId;
      const atcBtn = buttonsContainer.querySelector(".atc-button");
      if (atcBtn) {
        const isInCart = this.cartState.find(
          ({ productId }) => productId === product.variantId,
        );
        const isOutOfStock = product.outOfStock;
        atcBtn.classList.toggle("added-to-cart", !!isInCart);
        atcBtn.classList.toggle("disabled", isOutOfStock);
        atcBtn.disabled = isOutOfStock;

        const addSpan = atcBtn.querySelector(".atc-button__add");
        if (addSpan) {
          addSpan.textContent = isOutOfStock ? "Out Of Stock" : "+ Add to cart";
        }
      }
    }
  }

  initModalContent() {
    const contentEl = this.modalContent;

    this.initializeModalSlider(contentEl);
    this.initializeModalImageZoom(contentEl);

    contentEl.querySelectorAll(".swiper-slide").forEach((page) => {
      if (page.querySelector(".selected-navigation-image")) {
        const navigationIndex = page.dataset.navigationIndex;
        const swiperInstance = contentEl.querySelector(".swiper")?.swiper;
        if (swiperInstance) {
          swiperInstance.slideTo(parseInt(navigationIndex));
        }
      }
    });

    const buttonsContainer = contentEl.querySelector(".quick-buy__buttons");
    if (!buttonsContainer) return;
    const atcBtn = buttonsContainer.querySelector(".atc-button");
    const modalProductId = parseInt(buttonsContainer.dataset.productId);

    const isInCart = this.cartState.find(
      ({ productId }) => productId === modalProductId,
    );

    if (atcBtn.classList.contains("fabric-samples-buy-atc")) {
      const isOutOfStock =
        atcBtn.querySelector(".atc-button__add")?.textContent.trim() ===
        "Out Of Stock";
      if (isOutOfStock) {
        atcBtn.classList.add("disabled");
      }
    }

    if (!atcBtn.dataset.listener) {
      atcBtn.classList.toggle("added-to-cart", !!isInCart);
      this.setQuickBuyAtcBtnListener(atcBtn);
      atcBtn.setAttribute("data-listener", true);
    }
  }

  initializeModalSlider(contentEl) {
    if (window.innerWidth < 981) return;

    const slider = contentEl.querySelector(
      ".fabric-samples-details-slider.swiper",
    );
    if (!slider) return;

    const slideCount = slider.querySelectorAll(".swiper-slide").length;
    if (slideCount <= 1) {
      slider.classList.add("swiper-no-pagination");
      const navContainer = slider.closest(".fabric-samples-details-navigation");
      if (navContainer) {
        navContainer.querySelector(".fabric-samples-details-previous")?.style.setProperty("visibility", "hidden");
        navContainer.querySelector(".fabric-samples-details-next")?.style.setProperty("visibility", "hidden");
      }
      return;
    }
    slider.classList.remove("swiper-no-pagination");

    if (slider.swiper) {
      slider.swiper.destroy(true, true);
    }

    const nextEl = slider.parentElement.querySelector(
      ".fabric-samples-details-next",
    );
    const prevEl = slider.parentElement.querySelector(
      ".fabric-samples-details-previous",
    );

    const navigationContainer = slider.closest(
      ".fabric-samples-details-navigation",
    );
    let paginationEl = null;

    if (navigationContainer) {
      let sibling = navigationContainer.nextElementSibling;
      while (sibling) {
        if (sibling.classList.contains("swiper-pagination")) {
          paginationEl = sibling;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      if (!paginationEl) {
        paginationEl = navigationContainer.parentElement.querySelector(
          ".swiper-pagination",
        );
      }
    }

    try {
      new Swiper(slider, {
        navigation: { nextEl, prevEl },
        observer: true,
        observeParents: true,
        slidesPerView: 1,
        breakpoints: {
          300: { slidesPerView: 7.2, pagination: false },
          981: {
            slidesPerView: 1,
            pagination:
              window.innerWidth >= 981 &&
              paginationEl &&
              paginationEl.classList.contains("swiper-pagination")
                ? { el: paginationEl, type: "fraction" }
                : false,
          },
        },
      });
    } catch (error) {
      console.error("Error initializing modal slider:", error);
    }
  }

  initializeModalImageZoom(contentEl) {
    const containers = contentEl.querySelectorAll(
      ".fabric-samples-image-container",
    );
    const isMobile = () => window.innerWidth <= 980;

    containers.forEach((container) => {
      const zoomIcon = container.querySelector(".fabric-samples-zoom-icon");
      const img = container.querySelector("img");
      if (!img) return;

      let isZoomed = false;
      let isDragging = false;
      let startX, startY, currentX, currentY;
      const ZOOM_SCALE = 2;
      const centerPosition = -(100 - 100 / ZOOM_SCALE) / 2;

      const toggleZoom = () => {
        isZoomed = !isZoomed;
        container.classList.toggle("is-zoomed", isZoomed);
        img.style.transform = isZoomed
          ? `scale(${ZOOM_SCALE}) translate(${centerPosition}%, ${centerPosition}%)`
          : "scale(1) translate(0, 0)";
      };

      if (zoomIcon) {
        zoomIcon.addEventListener("click", () => {
          if (!isMobile()) toggleZoom();
        });
      }

      container.addEventListener("click", (e) => {
        if (!isMobile()) return;
        toggleZoom();
        e.preventDefault();
        e.stopPropagation();
      });

      container.addEventListener("mousemove", (e) => {
        if (!isZoomed || isMobile()) return;
        const rect = container.getBoundingClientRect();
        const x = Math.max(
          0,
          Math.min(1, (e.clientX - rect.left) / rect.width),
        );
        const y = Math.max(
          0,
          Math.min(1, (e.clientY - rect.top) / rect.height),
        );
        const translateX = -(100 - 100 / ZOOM_SCALE) * x;
        const translateY = -(100 - 100 / ZOOM_SCALE) * y;
        img.style.transform = `scale(${ZOOM_SCALE}) translate(${translateX}%, ${translateY}%)`;
      });

      container.addEventListener("mouseleave", () => {
        if (!isZoomed || isMobile()) return;
        img.style.transform = `scale(${ZOOM_SCALE}) translate(${centerPosition}%, ${centerPosition}%)`;
      });

      container.addEventListener(
        "touchstart",
        (e) => {
          if (!isZoomed) return;
          isDragging = true;
          const touch = e.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          const style = window.getComputedStyle(img);
          const matrix = new DOMMatrix(style.transform);
          currentX = (matrix.m41 / (matrix.m11 * img.offsetWidth)) * 100;
          currentY = (matrix.m42 / (matrix.m11 * img.offsetHeight)) * 100;
          e.stopPropagation();
        },
        { passive: false },
      );

      container.addEventListener(
        "touchmove",
        (e) => {
          if (!isZoomed || !isDragging) return;
          const touch = e.touches[0];
          const maxTranslate = 100 - 100 / ZOOM_SCALE;
          const newX = Math.max(
            -maxTranslate,
            Math.min(
              0,
              currentX +
                ((touch.clientX - startX) / container.offsetWidth) * 115,
            ),
          );
          const newY = Math.max(
            -maxTranslate,
            Math.min(
              0,
              currentY +
                ((touch.clientY - startY) / container.offsetHeight) * 115,
            ),
          );
          img.style.transform = `scale(${ZOOM_SCALE}) translate(${newX}%, ${newY}%)`;
          e.preventDefault();
          e.stopPropagation();
        },
        { passive: false },
      );

      container.addEventListener("touchend", () => {
        isDragging = false;
      });
    });
  }

  toggleCartState(card, event) {
    const isAtcClick = event?.target.closest(".product-card__atc-btn");

    if (isAtcClick) {
      event.stopPropagation();
      if (isAtcClick.classList.contains("disabled")) {
        return;
      }
    }

    if (!isAtcClick && event?.target.closest("[data-fancybox-trigger]")) {
      return;
    }

    if (!isAtcClick && event?.target.closest("[data-switch-product-id]")) {
      return;
    }

    const productId = parseInt(card.dataset.productId);
    const productTitle = card.dataset.productLabel;
    const isOutOfStock = card.dataset.outOfStock === "true";

    if (isOutOfStock) {
      return;
    }

    const productCardImageHtml = card.querySelector(
      ".product-card__image-wrapper>img",
    ).outerHTML;
    const productCardTitleHtml = card.querySelector(
      ".product-card__title",
    ).outerHTML;
    const cartSwatchElement = `<div class="swatch__container fabric-samples-cart-item" data-product-id="${productId}">
                                ${productCardImageHtml}
                                <div class="fabric-samples-product-title">
                                  ${productTitle}
                                </div>
                                <button type="button" class="fabric-samples-cart-remove" aria-label="Remove ${productTitle} from cart">
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                  </svg>
                                </button>
                              </div>`;
    const cartSwatchElementMobile = `<div class="swatch__container-mobile" data-product-id="${productId}">
                                        <div class="row__content">
                                          ${productCardImageHtml}
                                          ${productCardTitleHtml}
                                        </div>
                                        <button type="button" class="fabric-samples-cart-remove remove-icon" aria-label="Remove ${productTitle} from cart">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                                          </svg>
                                        </button>
                                      </div>`;
    const cartObject = {
      productId,
      cartSwatchElement,
      cartSwatchElementMobile,
    };

    const objectInCartState = this.cartState.find(
      ({ productId }) => productId === cartObject.productId,
    );

    if (
      this.cartState.find(({ productId }) => productId === cartObject.productId)
    ) {
      this.cartState.splice(this.cartState.indexOf(objectInCartState), 1);
      this.toggleCardState(card, false);
    } else {
      if (this.cartState.length >= this.maxProductInCart) {
        const limitModal = document.getElementById("reached-limit-modal");
        if (limitModal) limitModal.hidden = false;
        return;
      }
      this.cartState.push(cartObject);
      this.toggleCardState(card, true);
    }

    this.updateCartBar();
  }

  updateCartBar() {
    const details = document.querySelector(".collapsible-row details");
    const swatchElements = this.cartState.map(
      (object) => object.cartSwatchElement,
    );
    const swatchElementsMobile = this.cartState.map(
      (object) => object.cartSwatchElementMobile,
    );
    const emptyCartMessage = this.cartBar.querySelector(
      ".cart-container__background",
    );
    const cartCount = this.cartBar.querySelector(".fabric-samples-cart-count");

    cartCount.textContent = this.cartState.length;
    emptyCartMessage.classList.toggle(
      "visually-hidden",
      swatchElements.length > 0,
    );

    this.emptyBarSwatchesText.classList.toggle(
      "visually-hidden",
      swatchElementsMobile.length > 0,
    );
    this.orderSwatchesBtn.disabled =
      swatchElementsMobile.length > 0 ? false : true;

    this.orderSwatchesBtn.disabled = this.cartState.length === 0;

    const atcButtons = this.container.querySelectorAll(
      ".product-card-container__product-card:not(.added-to-cart) .product-card__atc-btn",
    );
    atcButtons.forEach((btn) => {
      btn.classList.toggle(
        "disabled",
        this.cartState.length >= this.maxProductInCart,
      );
    });

    if (
      this.productsContainerSwatches.classList.contains("close-animation") &&
      swatchElementsMobile.length <= 0
    ) {
      this.productsContainerSwatches.classList.remove("close-animation");
    }
    if (details.open && swatchElementsMobile.length == 1) {
      this.productsContainerSwatches.classList.add("open-animation");
    }
    this.cartBarSwatchesContainer.innerHTML = swatchElements.join("");
    this.cartBarSwatchesMobileContainer.innerHTML =
      swatchElementsMobile.join("");
  }

  initProductInCart() {
    this.cartBarSwatchesContainer
      .querySelectorAll(".swatch__container")
      .forEach((cartSwatch) => {
        const productId = cartSwatch.dataset.productId;
        const productCard = this.container.querySelector(
          `.product-card-container__product-card[data-product-id="${productId}"]`,
        );
        if (productCard) this.toggleCartState(productCard);
      });

    document.querySelector(".fabric-samples-results").innerHTML =
      `${this.container.querySelectorAll(".fabric-samples-card").length} Results`;
  }

  removeProductFromCart(event) {
    const removeBtn = event.target.closest(".fabric-samples-cart-remove");
    if (
      removeBtn ||
      event.target.closest(".swatch__container") ||
      event.target.classList.contains("swatch__container")
    ) {
      const productToRemove = removeBtn
        ? removeBtn.closest(".swatch__container") || removeBtn.closest(".swatch__container-mobile")
        : event.target.classList.contains("swatch__container")
          ? event.target
          : event.target.closest(".swatch__container");
      if (!productToRemove) return;
      const idProductToRemove = productToRemove.dataset.productId;

      const objectInCartState = this.cartState.find(
        ({ productId }) => productId === parseInt(idProductToRemove),
      );
      const productCard = this.container.querySelector(
        `[data-product-id="${idProductToRemove}"]`,
      );
      this.toggleCardState(productCard, false);

      this.cartState.splice(this.cartState.indexOf(objectInCartState), 1);
      this.updateCartBar();
      return;
    }
    if (event.target.closest(".remove-icon")) {
      const productToRemove = event.target.closest(".swatch__container-mobile");
      if (!productToRemove) return;

      const idProductToRemove = productToRemove.dataset.productId;

      const objectInCartState = this.cartState.find(
        ({ productId }) => productId === parseInt(idProductToRemove),
      );
      const productCard = this.container.querySelector(
        `[data-product-id="${idProductToRemove}"]`,
      );
      this.toggleCardState(productCard, false);

      this.cartState.splice(this.cartState.indexOf(objectInCartState), 1);
      this.updateCartBar();
    }
  }

  toggleCardState(card, enable) {
    card?.classList.toggle("added-to-cart", enable);

    const productId = card?.dataset.productId;
    const quickBuyButtonContainers = document.querySelectorAll(
      `.quick-buy__buttons[data-product-id="${productId}"]`,
    );

    quickBuyButtonContainers &&
      quickBuyButtonContainers.forEach((container) =>
        container
          .querySelector(".atc-button")
          .classList.toggle("added-to-cart", enable),
      );
  }

  addResizeListener() {
    let resizeTimeout;

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        const modal = document.getElementById("fabric-samples-modal");
        if (modal && !modal.hidden) {
          this.destroyModalSwiper();
          this.initModalContent();
        }
        this.syncDesktopCartFadeObserver();
      }, 250);
    });
  }

  initDesktopCartFadeObserver() {
    if (!(this.cartBar instanceof HTMLElement)) return;

    this.desktopCartFadeTarget = document.querySelector(
      ".metafield-rich_text_field",
    );
    if (!(this.desktopCartFadeTarget instanceof HTMLElement)) return;

    this.cartBar.style.transition = "opacity 250ms ease";
    this.cartBar.style.opacity = "1";
    this.cartBar.style.visibility = "visible";

    this.syncDesktopCartFadeObserver();
  }

  syncDesktopCartFadeObserver() {
    if (!this.desktopCartFadeTarget) return;

    if (window.innerWidth < 981) {
      this.disconnectDesktopCartFadeObserver();
      this.setDesktopCartVisibility(true, true);
      return;
    }

    if (this.desktopCartFadeObserver) return;

    const desktopFadeMargin = -150;

    this.desktopCartFadeObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        const observerBottomOffset = desktopFadeMargin;
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        const observerBottomBoundary = viewportHeight + observerBottomOffset;
        const isTargetBelowObserverBoundary =
          entry.boundingClientRect.top >= observerBottomBoundary;
        const shouldShowCart =
          !entry.isIntersecting && isTargetBelowObserverBoundary;

        this.setDesktopCartVisibility(shouldShowCart);
      },
      {
        root: null,
        rootMargin: `0px 0px ${desktopFadeMargin}px 0px`,
        threshold: 0,
      },
    );

    this.desktopCartFadeObserver.observe(this.desktopCartFadeTarget);
  }

  setDesktopCartVisibility(shouldShow, force = false) {
    if (!(this.cartBar instanceof HTMLElement)) return;

    this.desktopCartShouldShow = shouldShow;

    if (this.desktopCartFadeTransitionHandler) {
      this.cartBar.removeEventListener(
        "transitionend",
        this.desktopCartFadeTransitionHandler,
      );
      this.desktopCartFadeTransitionHandler = null;
    }

    if (shouldShow) {
      this.cartBar.style.visibility = "visible";
      if (force) {
        this.cartBar.style.opacity = "1";
        return;
      }

      requestAnimationFrame(() => {
        if (this.desktopCartShouldShow) {
          this.cartBar.style.opacity = "1";
        }
      });
      return;
    }

    if (force) {
      this.cartBar.style.opacity = "0";
      this.cartBar.style.visibility = "hidden";
      return;
    }

    this.cartBar.style.opacity = "0";
    this.desktopCartFadeTransitionHandler = (event) => {
      if (event.propertyName !== "opacity") return;
      if (!this.desktopCartShouldShow) {
        this.cartBar.style.visibility = "hidden";
      }

      this.cartBar.removeEventListener(
        "transitionend",
        this.desktopCartFadeTransitionHandler,
      );
      this.desktopCartFadeTransitionHandler = null;
    };
    this.cartBar.addEventListener(
      "transitionend",
      this.desktopCartFadeTransitionHandler,
    );
  }

  disconnectDesktopCartFadeObserver() {
    if (!this.desktopCartFadeObserver) return;

    this.desktopCartFadeObserver.disconnect();
    this.desktopCartFadeObserver = null;
  }

  async submitCart(event) {
    event.preventDefault();
    if (this.cartState.length === 0) return;
    let addToCartData = {
      items: this.cartState.map(({ productId }) => ({
        quantity: "1",
        id: productId,
        properties: {
          _swatchesCollection: true,
        },
      })),
    };

    await this.checkIfSwatchInCart();

    const params = {
      method: "POST",
      body: JSON.stringify(addToCartData),
      headers: {
        "Content-Type": "application/json",
      },
    };

    fetch("/cart/add.js", params).then(
      () => (window.location.href = "/checkout"),
    );
  }

  async checkIfSwatchInCart() {
    function convertArrayToObject(arr) {
      return arr.reduce((obj, item) => {
        obj[item.id] = 0;
        return obj;
      }, {});
    }
    const request = await fetch("/cart.js");
    const data = await request.json();
    const swatchesToRemove = data.items.filter(
      (item) => item.properties._swatchesCollection,
    );
    if (swatchesToRemove.length === 0) return;
    const swatchesToRemoveData = {
      updates: convertArrayToObject(swatchesToRemove),
    };

    const params = {
      method: "POST",
      body: JSON.stringify(swatchesToRemoveData),
      headers: {
        "Content-Type": "application/json",
      },
    };
    await fetch("/cart/update.js", params);
  }
}

customElements.define("collection-fabric-samples", CollectionFabricSamples);
